import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import LogHandler from "../../handlers/LogHandler";
import XLSX from "xlsx";
export default class ExportUtils {

    public static async export(params: ExportParams): Promise<void> {
        const data = await ExportUtils.prepareDataForExport(params);
        if (data.length === 0) {
            console.warn("No data available for export.");
            return;
        }

        const headers = params.columnHeaderMap
            ? Object.values(params.columnHeaderMap)
            : Object.keys(data[0]);

        if (headers.length === 0) {
            console.warn("No headers provided for export, using first row of data.");
        }

        if (params.toClipboard) {
            const csvContent = ExportUtils.generateCSV(data, headers, params.columnHeaderMap, params.delimiter || ";");
            await ExportUtils.copyToClipboard(csvContent);
        } else {
            ExportUtils.generateXLSX(data, headers, params.columnHeaderMap, params.filename || "export.xlsx");
        }
    }

    private static async prepareDataForExport(params: ExportParams): Promise<Array<Record<string, string>>> {
        if (params.data) {
            return ExportUtils.extractDataFromArray(params.data, params.expand);
        } else if (params.oDataListBinding) {
            return await ExportUtils.extractDataFromODataBinding(params.oDataListBinding, params.expand);
        } else {
            console.error("No data or ODataListBinding provided for export.");
            return [];
        }
    }

    private static extractDataFromArray(data: Array<Record<string, string | number | boolean | object>>, expand: string[]): Array<Record<string, string>> {
        return data.map(item => ExportUtils.flattenObject(item, expand));
    }

    private static flattenObject(item: Record<string, string | number | boolean | object>, expand: string[]): Record<string, string> {
        const flattened: Record<string, string> = {};
        ExportUtils.flatten(item, flattened, expand);
        return flattened;
    }

    private static flatten(
        obj: Record<string, string | number | boolean | object>,
        flattened: Record<string, string> = {},
        expand: string[],
        parentKey: string = ""
    ): void {
        Object.entries(obj).forEach(([key, value]) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (typeof value === "object" && value !== null && !Array.isArray(value)) {
                // Recursively flatten nested objects
                ExportUtils.flatten(value as Record<string, string | number | boolean | object>, flattened, expand, fullKey);
            } else {
                // Add the value to the flattened object
                if (expand.includes(key) || !parentKey) {
                    let stringedValue = "";
                    if (value instanceof Object) {
                        // If the value is an object, stringify it
                        stringedValue = JSON.stringify(value);
                    }
                    else {
                        // Otherwise, convert it to string
                        stringedValue = String(value);
                    }
                    flattened[fullKey] = stringedValue;                    
                }
            }
        });
    }

    private static async extractDataFromODataBinding(binding: ODataListBinding, expand: string[]): Promise<Array<Record<string, string>>> {
        const data = await binding.requestContexts();
        const results = await Promise.all(data.map(async context => {
            const item = await context.requestObject() as Record<string, string | number | boolean | object>;
            return ExportUtils.flattenObject(item, expand);
        }));
        return results;
    }

    private static generateCSV(
        data: Array<Record<string, string>>,
        headers: string[],
        columnHeaderMap: Record<string, string> | undefined,
        delimiter: string
    ): string {
        const csvRows: string[] = [];
        csvRows.push(headers.join(delimiter));

        data.forEach(row => {
            const values = headers.map(header => {
                const field = columnHeaderMap
                    ? Object.keys(columnHeaderMap).find(key => columnHeaderMap[key] === header)
                    : header;
                return field && row[field] !== undefined ? row[field] : "";
            });
            csvRows.push(values.join(delimiter));
        });

        return csvRows.join("\n");
    }

    private static async copyToClipboard(content: string): Promise<void> {
        try {
            await navigator.clipboard.writeText(content);
            LogHandler.display("CSV content copied to clipboard.");
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
        }
    }

    private static generateXLSX(
        data: Array<Record<string, string>>,
        headers: string[],
        columnHeaderMap: Record<string, string> | undefined,
        filename: string
    ): void {
        const workbook = XLSX.utils.book_new();
        const worksheetData = [
            headers,
            ...data.map(row =>
                headers.map(header => {
                    const field = columnHeaderMap
                        ? Object.keys(columnHeaderMap).find(key => columnHeaderMap[key] === header)
                        : header;
                    return field && row[field] !== undefined ? row[field] : "";
                })
            )
        ];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Export");
        XLSX.writeFile(workbook, filename);
        LogHandler.display(`Excel file ${filename} generated successfully.`);
    }
}
export type ExportParams = {
    data?: Array<Record<string, string | number | boolean | object>>; // Array of objects to export
    oDataListBinding?: ODataListBinding; // ListBinding or similar
    columnHeaderMap?: Record<string, string>; // if not provided, will use the first row of data
    filename?: string; // default is 'export.xlsx'
    delimiter?: string; // default is ';'
    expand: string[]; // Will add these as flattened columns to the export
    toClipboard: boolean; // Whether to create an excel file or copy a CSV to the clipboard 
};